// src/component/SidePanel.js
import React from 'react';
import { FaBars, FaTimes, FaSpinner, FaExclamationTriangle, FaSync } from 'react-icons/fa'; // Import necessary icons

/**
 * SidePanel Component
 * Displays a list of chat sessions and allows selection.
 * Can be toggled open/closed. Includes inline CSS via styled-jsx.
 *
 * Props:
 * - isOpen (boolean): Whether the panel is currently visible.
 * - togglePanel (function): Function to call when the toggle button is clicked.
 * - sessions (array): List of session objects, e.g., [{ id, title, lastUpdatedAt, createdAt }]
 * - onSelectSession (function): Function to call with the session ID when a session is clicked.
 * - activeSessionId (number|string|null): The ID of the currently active/selected session.
 * - isLoading (boolean): True if the session list is currently being fetched.
 * - error (string|null): Error message related to fetching sessions, if any.
 * - onRefreshSessions (function, optional): Function to manually trigger a refresh of the session list.
 */
const SidePanel = ({
    isOpen,
    togglePanel,
    sessions,
    onSelectSession,
    activeSessionId,
    isLoading,
    error,
    onRefreshSessions // Optional prop for refresh functionality
}) => {

    // Helper function to format date strings into a readable format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            // Check if the date is valid after parsing
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            // Example Format: "Sep 21 09:30 AM"
            return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' +
                   date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
        } catch (e) {
            console.error("Error formatting date:", dateString, e);
            return 'Invalid Date';
        }
    };

    return (
        <>
            {/* Toggle Button: Stays visible to open/close the panel */}
            <button
                onClick={togglePanel}
                className={`side-panel-toggle-button ${isOpen ? 'is-open' : ''}`}
                aria-label={isOpen ? "Close session panel" : "Open session panel"}
                title={isOpen ? "Close session panel" : "Open session panel"}
                aria-controls="chat-session-panel"
                aria-expanded={isOpen}
            >
                {isOpen ? <FaTimes /> : <FaBars />}
            </button>

            {/* The Panel: Slides in/out based on isOpen state */}
            <div
                id="chat-session-panel"
                className={`side-panel ${isOpen ? 'open' : 'closed'}`}
                style={{ visibility: isOpen ? 'visible' : 'hidden' }} // Control visibility for accessibility
            >
                {/* Panel Header */}
                <div className="side-panel-header">
                    <h2>Chat History</h2>
                    {/* Optional Refresh Button */}
                    {onRefreshSessions && (
                        <button
                            onClick={onRefreshSessions}
                            className="refresh-button"
                            title="Refresh Sessions"
                            disabled={isLoading}
                            aria-label="Refresh chat sessions"
                        >
                           {isLoading ? <FaSpinner className="spin-fast" /> : <FaSync />}
                        </button>
                    )}
                </div>

                {/* Panel Content: Shows loading, error, empty, or the list */}
                <div className="side-panel-content">
                    {isLoading && (
                        <div className="loading-state">
                            <FaSpinner className="spin" /> Loading sessions...
                        </div>
                    )}
                    {error && !isLoading && (
                        <div className="error-state">
                            <FaExclamationTriangle />
                            <p>Error loading sessions:</p>
                            <p className="error-details">{error}</p>
                             {onRefreshSessions && (
                                <button onClick={onRefreshSessions} className="retry-button">
                                    Try Again
                                </button>
                             )}
                        </div>
                    )}
                    {!isLoading && !error && sessions.length === 0 && (
                        <div className="empty-state">
                            No chat history found. Start a new chat!
                        </div>
                    )}
                    {!isLoading && !error && sessions.length > 0 && (
                        <ul className="sessions-list" aria-label="Chat sessions">
                            {sessions.map(session => (
                                <li
                                    key={session.id}
                                    className={`session-item ${session.id === activeSessionId ? 'active' : ''}`}
                                    onClick={() => onSelectSession(session.id)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelectSession(session.id); }}
                                    aria-current={session.id === activeSessionId ? 'page' : undefined}
                                >
                                    <span className="session-title" title={session.title || `Session ${session.id}`}>
                                        {session.title || `Session ${session.id}`}
                                    </span>
                                    <span className="session-timestamp">
                                        {formatDate(session.lastUpdatedAt)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Inline CSS using styled-jsx */}
            <style jsx>{`
                /* --- Side Panel Toggle Button --- */
                .side-panel-toggle-button {
                  position: fixed; /* Or absolute relative to the main container */
                  top: 15px;
                  left: 15px;
                  z-index: 1300; /* Above panel and potentially other headers */
                  background-color: #1a237e;
                  color: white;
                  border: none;
                  border-radius: 50%;
                  width: 40px;
                  height: 40px;
                  font-size: 1.2rem;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
                  transition: background-color 0.3s, transform 0.3s ease-in-out;
                }

                .side-panel-toggle-button:hover {
                  background-color: #0d1757;
                }

                /* --- Side Panel Container --- */
                .side-panel {
                  width: 280px; /* Fixed width for the panel */
                  height: 100vh; /* Full viewport height */
                  position: fixed; /* Fixed position */
                  top: 0;
                  left: 0;
                  background-color: #ffffff; /* White background */
                  border-right: 1px solid #e0e4ec; /* Subtle border */
                  box-shadow: 2px 0 5px rgba(0,0,0,0.1); /* Soft shadow */
                  display: flex;
                  flex-direction: column;
                  z-index: 1200; /* Ensure it's above main content but below toggle */
                  transition: transform 0.3s ease-in-out, visibility 0.3s ease-in-out;
                  transform: translateX(-100%); /* Start hidden */
                  /* visibility: hidden; /* Start hidden for accessibility */
                  /* NOTE: Inline style handles visibility based on isOpen prop */
                }

                .side-panel.open {
                  transform: translateX(0); /* Slide in */
                  /* visibility: visible; */
                }

                .side-panel.closed {
                  transform: translateX(-100%); /* Slide out */
                   /* visibility: hidden; */
                }

                /* --- Panel Header --- */
                .side-panel-header {
                  padding: 15px 20px;
                  border-bottom: 1px solid #e0e4ec;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  flex-shrink: 0; /* Prevent header from shrinking */
                }

                .side-panel-header {
                 /* Instead of padding: 15px 20px; */
                 padding: 35px 20px 15px 20px; /* Increased top padding to 25px */
                 border-bottom: 1px solid #e0e4ec;
                 display: flex;
                 justify-content: space-between;
                 align-items: center;
                 flex-shrink: 0;
                }

                .refresh-button {
                  background: none;
                  border: none;
                  color: #5f6368;
                  font-size: 1.1rem;
                  cursor: pointer;
                  padding: 5px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  transition: background-color 0.2s, color 0.2s;
                }
                .refresh-button:hover:not(:disabled) {
                   background-color: #f1f3f4;
                   color: #1a237e;
                }
                .refresh-button:disabled {
                    color: #bdbdbd;
                    cursor: not-allowed;
                }
                .refresh-button .spin-fast { /* Faster spin for refresh */
                   animation: spin 0.8s linear infinite;
                }


                /* --- Panel Content Area --- */
                .side-panel-content {
                  flex-grow: 1; /* Take remaining space */
                  overflow-y: auto; /* Allow scrolling for the list */
                  padding-top: 10px;
                  padding-bottom: 10px;
                }

                /* --- Sessions List --- */
                .sessions-list {
                  list-style: none;
                  padding: 0;
                  margin: 0;
                }

                .session-item {
                  padding: 12px 20px;
                  cursor: pointer;
                  border-bottom: 1px solid #f0f2f5; /* Light separator */
                  transition: background-color 0.2s;
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  outline: none; /* Remove default focus outline, manage manually if needed */
                }
                .session-item:last-child {
                    border-bottom: none; /* No border for the last item */
                }

                .session-item:hover,
                .session-item:focus { /* Style focus similar to hover */
                  background-color: #f5f7fa; /* Light background on hover/focus */
                }

                .session-item.active {
                  background-color: #e3f2fd; /* Highlight active session */
                  border-left: 4px solid #1a237e; /* Distinctive active indicator */
                  padding-left: 16px; /* Adjust padding to account for border */
                }
                .session-item.active:hover,
                .session-item.active:focus {
                    background-color: #d_0e_af_f_8; /* Slightly darker blue on hover/focus when active */
                }

                .session-item.active .session-title {
                    font-weight: 600; /* Make active title bold */
                    color: #1976d2; /* Match theme */
                }


                .session-title {
                    font-size: 0.9rem;
                    color: #333;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis; /* Add ... for long titles */
                    line-height: 1.3;
                }

                .session-timestamp {
                    font-size: 0.75rem;
                    color: #6c757d; /* Grey color for timestamp */
                }


                /* --- Loading/Error/Empty States --- */
                .loading-state,
                .error-state,
                .empty-state {
                    padding: 30px 20px; /* More padding for these states */
                    text-align: center;
                    color: #6c757d;
                    font-size: 0.9rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px; /* Space between icon and text */
                }
                .error-state {
                    color: #c62828; /* Red color for errors */
                }
                .error-state .error-details {
                    font-size: 0.85rem;
                    color: #d32f2f;
                    max-width: 90%;
                    word-wrap: break-word; /* Wrap long error messages */
                }

                 .retry-button {
                    margin-top: 10px;
                    padding: 6px 12px;
                    font-size: 0.85rem;
                    background-color: #1a237e;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: background-color 0.2s;
                 }
                 .retry-button:hover {
                    background-color: #0d1757;
                 }

                /* --- General Spinner Animation --- */
                .spin {
                  animation: spin 1.5s linear infinite;
                  font-size: 1.5rem; /* Adjust size as needed */
                  line-height: 1;
                }

                /* Define the spin animation */
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default SidePanel;